import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { ProductoApiService } from '../services/producto-api.service';
import { ProductoCurrencyRateApiService } from '../services/producto-currency-rate-api.service';
import { CurrencyResponse } from '../models/producto.model';
import {
  BaseCurrencyResponse,
  ProductCurrencyRateRequest,
  ProductCurrencyRateResponse
} from '../models/producto-currency-rate.model';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';

/**
 * Administración de la moneda base y tipos de cambio del negocio (Incidente #52 — submódulo Monedas).
 * Los precios de productos se resuelven usando estas tasas (rateToBase).
 * businessId lo infiere el backend desde el JWT — no se envía por query.
 */
@Component({
  selector: 'app-producto-currency-rates',
  standalone: true,
  imports: [CommonModule, FormsModule, PageBreadcrumbComponent, BadgeComponent],
  templateUrl: './producto-currency-rates.component.html'
})
export class ProductoCurrencyRatesComponent implements OnInit {
  currencies: CurrencyResponse[] = [];
  rates: ProductCurrencyRateResponse[] = [];
  baseCurrency: BaseCurrencyResponse | null = null;

  loading = false;
  error = '';

  // Moneda base
  selectedBaseCurrencyId: number | null = null;
  savingBase = false;

  // Form de tasa (crear/editar). En edición currencyId queda fijo (upsert por moneda).
  showForm = false;
  editing = false;
  formCurrencyId: number | null = null;
  formRateToBase: number | null = null;
  formIsActive = true;
  formError = '';
  saving = false;

  togglingId: number | null = null;

  constructor(
    private productApi: ProductoApiService,
    private rateApi: ProductoCurrencyRateApiService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.cargarMonedas();
    this.cargarBase();
    this.cargarTasas();
  }

  private cargarMonedas(): void {
    this.productApi.getCurrencies().subscribe({
      next: (data) => this.currencies = data,
      error: () => {}
    });
  }

  cargarBase(): void {
    this.rateApi.getBaseCurrency().subscribe({
      next: (data) => {
        this.baseCurrency = data;
        this.selectedBaseCurrencyId = data?.currencyId ?? null;
      },
      error: () => { this.baseCurrency = null; }
    });
  }

  cargarTasas(): void {
    this.loading = true;
    this.error = '';
    this.rateApi.getRates().subscribe({
      next: (data) => { this.rates = data; this.loading = false; },
      error: (err) => {
        this.rates = [];
        this.loading = false;
        this.error = err.error?.message || 'Error al cargar los tipos de cambio';
      }
    });
  }

  // ---- Moneda base ----

  guardarBase(): void {
    if (!this.selectedBaseCurrencyId) return;
    this.savingBase = true;
    this.rateApi.setBaseCurrency(this.selectedBaseCurrencyId).subscribe({
      next: (data) => {
        this.baseCurrency = data;
        this.savingBase = false;
        this.toast.updated('Moneda base');
        this.cargarTasas();
      },
      error: (err) => {
        this.savingBase = false;
        const msg = err.error?.message || 'No se pudo fijar la moneda base';
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  // ---- Tasas ----

  getCurrencyLabel(currencyId: number): string {
    const c = this.currencies.find(x => x.id === currencyId);
    return c ? `${c.name} (${c.code})` : `#${currencyId}`;
  }

  /** Monedas disponibles para crear una tasa nueva (excluye las que ya tienen tasa). */
  get monedasDisponibles(): CurrencyResponse[] {
    const usados = new Set(this.rates.map(r => r.currencyId));
    return this.currencies.filter(c => !usados.has(c.id));
  }

  nuevaTasa(): void {
    this.editing = false;
    this.formCurrencyId = null;
    this.formRateToBase = null;
    this.formIsActive = true;
    this.formError = '';
    this.showForm = true;
  }

  editarTasa(rate: ProductCurrencyRateResponse): void {
    this.editing = true;
    this.formCurrencyId = rate.currencyId;
    this.formRateToBase = rate.rateToBase;
    this.formIsActive = rate.isActive;
    this.formError = '';
    this.showForm = true;
  }

  cerrarForm(): void {
    this.showForm = false;
    this.formError = '';
  }

  guardarTasa(): void {
    if (!this.formCurrencyId) {
      this.formError = 'Seleccione una moneda';
      return;
    }
    if (this.formRateToBase === null || this.formRateToBase <= 0) {
      this.formError = 'El tipo de cambio debe ser mayor que cero';
      return;
    }
    this.saving = true;
    this.formError = '';
    const wasEditing = this.editing;

    const req: ProductCurrencyRateRequest = {
      currencyId: this.formCurrencyId,
      rateToBase: this.formRateToBase,
      isActive: this.formIsActive
    };

    this.rateApi.upsertRate(req).subscribe({
      next: () => {
        this.saving = false;
        this.cerrarForm();
        if (wasEditing) {
          this.toast.updated('Tipo de cambio');
        } else {
          this.toast.created('Tipo de cambio');
        }
        this.cargarTasas();
      },
      error: (err) => {
        this.saving = false;
        this.formError = err.error?.message || 'No se pudo guardar el tipo de cambio';
      }
    });
  }

  async toggleActivo(rate: ProductCurrencyRateResponse): Promise<void> {
    const nuevoEstado = !rate.isActive;
    this.togglingId = rate.id;
    const req: ProductCurrencyRateRequest = {
      currencyId: rate.currencyId,
      rateToBase: rate.rateToBase,
      isActive: nuevoEstado
    };
    this.rateApi.upsertRate(req).subscribe({
      next: (updated) => {
        rate.isActive = updated.isActive;
        rate.datetimeUpdated = updated.datetimeUpdated;
        this.togglingId = null;
        this.toast.success({ title: 'Listo', message: `Tipo de cambio ${nuevoEstado ? 'activado' : 'desactivado'}` });
      },
      error: (err) => {
        this.togglingId = null;
        this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo cambiar el estado' });
      }
    });
  }

  async eliminarTasa(rate: ProductCurrencyRateResponse): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar tipo de cambio',
      message: `¿Eliminar el tipo de cambio de ${this.getCurrencyLabel(rate.currencyId)}?`
    });
    if (!ok) return;
    this.rateApi.deleteRate(rate.currencyId).subscribe({
      next: () => {
        this.cargarTasas();
        this.toast.deleted('Tipo de cambio');
      },
      error: (err) => {
        this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo eliminar el tipo de cambio' });
      }
    });
  }
}
